
import { Vue, Component, Prop } from 'vue-property-decorator';
import { IFilter } from '@/types';
import AutocompleteListItem from "@/components/base/form/AutocompleteListItem.vue";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import SvgArrow from "@/assets/icons/arrow-down.svg";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import store from '@/store';

@Component({
    components: { BaseTooltip, SvgArrow, AutocompleteListItem,TrashBtn },
})

export default class AutocompleteFilter extends Vue {
    @Prop({ required: true }) filter!: IFilter;
    @Prop({ type: Boolean, default: true }) readonly isUsedBlurAutocomplete!: boolean;

    scrollContainer: Element | null = null;
    scrollHandler: (() => void) | null = null;
    mutationObserver: MutationObserver | null = null;

    get currentUserId(): number | undefined {
        const user = store.getters['authModule/GET_USER'] as { account_id?: number; id?: number } | undefined;
        return user?.account_id ?? user?.id;
    }

    get currentUserAsItem(): any {
        const user = store.getters['authModule/GET_USER'] as { account_id?: number; id?: number; first_name?: string; last_name?: string } | undefined;
        const itemValue = this.filter.itemValue;
        const itemText = this.filter.itemText || 'name';
        if (!user || this.currentUserId == null || !itemValue) return null;
        const id = this.currentUserId;
        const name = `${id} ${user.first_name || ''} ${user.last_name || ''}`.trim();
        return { [itemValue]: id, [itemText]: name };
    }

    get sortedItems(): any[] {
        const itemValue = this.filter.itemValue;
        const items = Array.isArray(this.filter.items) ? [...this.filter.items] : [];
        if (!this.filter.pinCurrentUser || this.currentUserId == null || !itemValue) {
            return items;
        }
        const currentUserItem = this.currentUserAsItem;
        if (!currentUserItem) return items;
        const hasCurrentUser = items.some((i: any) => i[itemValue] === this.currentUserId || i[itemValue] === Number(this.currentUserId));
        if (hasCurrentUser) {
            const idx = items.findIndex((i: any) => i[itemValue] === this.currentUserId || i[itemValue] === Number(this.currentUserId));
            if (idx <= 0) return items;
            const [pinned] = items.splice(idx, 1);
            return [pinned, ...items];
        }
        return [currentUserItem, ...items];
    }

    isPinnedUserItem(item: any): boolean {
        if (!this.filter.pinCurrentUser || this.currentUserId == null || !this.filter.itemValue) return false;
        const id = item[this.filter.itemValue];
        return id === this.currentUserId || id === Number(this.currentUserId);
    }

    get firstVisibleItem(): any {
        const itemValue = this.filter.itemValue;
        if (!this.filter.pinCurrentUser || !itemValue || !Array.isArray(this.filter.select) || !this.sortedItems.length) return null;
        const key = itemValue;
        const selectedIds = new Set((this.filter.select as any[]).map((s: any) => s && (s as Record<string, unknown>)[key]));
        const first = this.sortedItems.find((i: any) => !selectedIds.has((i as Record<string, unknown>)[key]));
        return first ?? null;
    }

    isItemFirstVisible(item: any): boolean {
        const first = this.firstVisibleItem;
        const key = this.filter.itemValue;
        return !!first && !!key && (item as Record<string, unknown>)[key] === (first as Record<string, unknown>)[key];
    }

    get isPinnedUserSelected(): boolean {
        if (!this.filter.pinCurrentUser || this.currentUserId == null || !Array.isArray(this.filter.select)) return false;
        const key = this.filter.itemValue;
        if (!key) return false;
        return (this.filter.select as any[]).some((s: any) => s && ((s as Record<string, unknown>)[key] === this.currentUserId || (s as Record<string, unknown>)[key] === Number(this.currentUserId)));
    }

    get selectedItemsWithPinnedFirst(): Array<{ type: 'item'; item: any } | { type: 'line' }> {
        const select = this.filter.select as any[] | undefined;
        const key = this.filter.itemValue;
        if (!this.filter.pinCurrentUser || this.currentUserId == null || !key || !Array.isArray(select) || !select.length) {
            return (select || []).map((item) => ({ type: 'item' as const, item }));
        }
        const result: Array<{ type: 'item'; item: any } | { type: 'line' }> = [];
        const pinned: any[] = [];
        const rest: any[] = [];
        for (const s of select) {
            if (!s) continue;
            const id = (s as Record<string, unknown>)[key];
            if (id === this.currentUserId || id === Number(this.currentUserId)) {
                pinned.push(s);
            } else {
                rest.push(s);
            }
        }
        pinned.forEach((item) => result.push({ type: 'item', item }));
        if (pinned.length > 0) result.push({ type: 'line' });
        rest.forEach((item) => result.push({ type: 'item', item }));
        return result;
    }

    deleteFilterChips(select: any[], id: string | number, filter: IFilter): void {
        if (Array.isArray(select) && filter.multiple) {
            const index = select.findIndex(i => i[filter.itemValue!] === id);
            select.splice(index, 1);
        } else {
            filter.select = '';
        }
        this.$emit('delete-item-autocomplete', filter);
    }

    clear(filter: IFilter): void {
        filter.clear();
    }

    get hideNoData(): boolean {
        if (this.filter.select?.length) return false;
        return  !this.filter.autocompleteValue || !!this.filter.loading;
    }

    updateSearch(filter: IFilter, value: string | null): void {
        if (typeof value !== 'string') return;
        if (filter.multiple && filter.autocompleteValue === null && !!filter.apiMethod) {
            filter.items = filter.select;
        }
        filter.autocompleteValue = value;
        const minSearchLength = filter.minSearchLength !== undefined ? filter.minSearchLength : 1;
        if (filter.autocompleteValue.length >= minSearchLength) {
            this.$emit('debounce-query-selections', filter.id);
        }
        if (filter.autocompleteValue?.length! <= 0 && !!filter.apiMethod) {
            filter.items = filter.select?.length! >= 0 ?  [...filter.select] : [];
        }
    }

    filterItems(item: any, queryText: string, filter: IFilter): boolean {
        const textOne = item[filter.itemText].toString().toLowerCase();
        const searchText = queryText.toLowerCase();
        if (!filter.alsoSearchBy) return textOne.indexOf(searchText) > -1;
        const textTwo = item[filter.alsoSearchBy].toLowerCase();
        return textOne.indexOf(searchText) > -1 || textTwo!.indexOf(searchText) > -1;
    }

    mounted(): void {
        this.mutationObserver = new MutationObserver(() => {
            if (this.filter.loadMore) {
                this.attachScrollListener();
            } else {
                this.detachScrollListener();
            }
        });
        this.mutationObserver.observe(this.$el, { childList: true, subtree: true });
    }

    beforeDestroy(): void {
        this.mutationObserver?.disconnect();
        this.mutationObserver = null;
        this.detachScrollListener();
    }

    attachScrollListener(): void {
        const container = this.$el.querySelector('.v-select-list');
        if (!container || container === this.scrollContainer) return;
        this.detachScrollListener();
        this.scrollContainer = container;
        this.scrollHandler = () => {
            const el = container as HTMLElement;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20 && !this.filter.isLoadingMore) {
                this.filter.loadMore?.();
            }
        };
        container.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    detachScrollListener(): void {
        if (this.scrollContainer && this.scrollHandler) {
            this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
        }
        this.scrollContainer = null;
        this.scrollHandler = null;
    }
}
